/**
 * Pure helpers of the ABCDior Settings screen, kept out of the component so
 * they can be unit-tested.
 */
import dayjs from 'dayjs';
import type { Store } from '@/shared/config/app-config';
import { getImage, STORAGE } from '@/shared/lib/image';

export const MESSAGE_LEVELS = [
  { name: 'Green', value: 'green' },
  { name: 'Grey', value: 'grey' },
  { name: 'Orange', value: 'orange' },
  { name: 'Red', value: 'red' },
] as const;

export const MIN_TABLE_ROWS = 5;

export const formatDate = (date: string | null | undefined): string =>
  date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';

/** Number of empty rows to add so a table always shows at least MIN_TABLE_ROWS rows. */
export const extraRows = (count: number): number =>
  Math.max(0, MIN_TABLE_ROWS - count);

/** Store objects for a list of ids; unknown ids become undefined. */
export const resolveStores = (
  ids: readonly (string | null)[] | null | undefined,
  stores: readonly Store[],
): (Store | undefined)[] =>
  (ids ?? []).map((id) =>
    stores.find((store) => String(store.id) === String(id)),
  );

/**
 * Label of the "Assigned Stores" column: the store name, or the country when
 * all stores share one, or the region, or "multiple selection".
 */
export const storesLabel = (
  stores: readonly (Store | undefined)[] | null | undefined,
): string => {
  if (!stores) return '';
  const assigned = stores.filter((store): store is Store => !!store);
  if (assigned.length === 0) return '';
  const first = assigned[0]!;
  if (assigned.length === 1) return first.name;
  if (assigned.every((store) => store.country === first.country))
    return String(first.country ?? '');
  if (assigned.every((store) => store.region === first.region))
    return String(first.region ?? '');
  return 'multiple selection';
};

/** Signed URL of the picture attached to a background, null when unknown or not signed in. */
export const backgroundPictureUrl = (
  item: { id?: string } | null | undefined,
): string | null =>
  item?.id
    ? getImage(
        'abc',
        STORAGE.AbcDiorBack,
        `/background-pictures/${item.id}-background`,
      )
    : null;

/** Selected store ids in the order of the stores sorted by name. */
export const orderStoreIds = (
  ids: readonly string[],
  stores: readonly Store[],
): string[] =>
  [...stores]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((store) => String(store.id))
    .filter((id) => ids.includes(id));
